import { Plus, X } from "lucide-react";
import { ReactNode } from "react";
import { CBQChoice } from "../../../../api/Quiz/InitCBQQuestion";
import { keyForIndex } from "./types";

interface ChoicesEditorProps {
  choices: CBQChoice[];
  onChange: (choices: CBQChoice[]) => void;
  // Per-choice control for marking correctness (radio for mcq, checkbox for
  // multi_response). Receives the choice key.
  renderCorrectControl: (key: string) => ReactNode;
}

// Shared choice list for mcq / multi_response. Keys are positional (A,B,C...)
// and re-derived on every add/remove so they stay contiguous.
const ChoicesEditor = ({
  choices,
  onChange,
  renderCorrectControl,
}: ChoicesEditorProps) => {
  const rekey = (list: CBQChoice[]): CBQChoice[] =>
    list.map((c, i) => ({ ...c, key: keyForIndex(i) }));

  const updateText = (i: number, text: string) => {
    const next = [...choices];
    next[i] = { ...next[i], text };
    onChange(next);
  };
  const remove = (i: number) =>
    onChange(rekey(choices.filter((_, idx) => idx !== i)));
  const add = () =>
    onChange(rekey([...choices, { key: "", text: "" }]));

  return (
    <div className="space-y-2">
      <label className="block font-medium text-burntBlack text-sm">
        Choices
      </label>
      {choices.map((c, i) => (
        <div key={i} className="flex items-center gap-2">
          {renderCorrectControl(c.key)}
          <span className="w-6 text-sm font-semibold text-gray-500">
            {c.key}
          </span>
          <input
            type="text"
            value={c.text}
            placeholder="Choice text"
            onChange={(e) => updateText(i, e.target.value)}
            className="flex-1 h-10 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
          <button
            type="button"
            onClick={() => remove(i)}
            className="flex-shrink-0 p-2 text-gray-400 hover:text-red-500"
            title="Remove choice"
          >
            <X size={16} />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        className="inline-flex items-center gap-1 text-sm text-[#4379EE] hover:underline"
      >
        <Plus size={14} /> Add choice
      </button>
    </div>
  );
};

export default ChoicesEditor;
